/**
 * PlanGate — Declarative plan-gating component (desktop / Tailwind).
 *
 * Functionally identical to BigxBookApp/src/plans/PlanGate.tsx.
 *   <PlanGate feature="staff_management"><InviteStaffButton /></PlanGate>
 *   <PlanGate feature="data_export" fallback={<span>PRO only</span>}>...</PlanGate>
 *   <PlanGate feature="commission_tracking" hideFallback>...</PlanGate>
 *   <PlanGate requireActive><CreateInvoiceButton /></PlanGate>
 */

import type { JSX, ReactNode } from 'react';
import { Lock, Snowflake } from 'lucide-react';
import { usePlanGate } from './usePlanGate';
import { type PlanFeature, PLAN_TIERS, type PlanTier, FEATURE_DISPLAY_NAMES } from './features';

// ─── UpgradePrompt ───────────────────────────────────────────────────────────

interface UpgradePromptProps {
  feature: PlanFeature;
  requiredTier: PlanTier;
  currentTier: PlanTier;
}

export function UpgradePrompt({ feature, requiredTier, currentTier }: UpgradePromptProps) {
  const name = FEATURE_DISPLAY_NAMES[feature] ?? feature;
  return (
    <div className="my-1 rounded-lg border border-stroke-hairline/60 bg-bg-subtle px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <Lock className="h-4 w-4 shrink-0 text-fg-tertiary" />
        <div className="min-w-0 flex-1">
          <div className="text-label font-medium text-fg-primary">{name}</div>
          <div className="text-xs text-fg-secondary">
            Upgrade to {requiredTier} to unlock this feature.
          </div>
        </div>
        <span className="rounded bg-bg-surface px-2 py-0.5 text-label uppercase text-fg-secondary">
          {currentTier}
        </span>
      </div>
    </div>
  );
}

/**
 * The toolbar-shaped form of {@link UpgradePrompt}.
 *
 * `UpgradePrompt` is a banner — a bordered card with a title, a sentence of
 * explanation and a tier badge. That is right at the top of a section and wrong
 * inline in a row of buttons, where it renders as a five-line block wedged
 * between two controls and takes more width than the actions it sits beside.
 *
 * Pass this as PlanGate's `fallback` in a toolbar: it keeps the control's shape
 * and position, so the row still reads as a row, and says what it is with a
 * lock plus the tier. The full explanation belongs on hover, not in the button.
 */
export function UpgradeChip({
  label,
  requiredTier,
}: {
  label: string;
  requiredTier: PlanTier;
}) {
  return (
    <span
      className="inline-flex h-9 shrink-0 cursor-not-allowed items-center gap-1.5 rounded-md border border-stroke-hairline/15 bg-bg-subtle px-3 text-body text-fg-tertiary"
      title={`${label} needs the ${requiredTier} plan.`}
    >
      <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  );
}

// ─── FrozenBanner ────────────────────────────────────────────────────────────

export function FrozenBanner() {
  return (
    <div className="my-1 flex items-center gap-2 rounded-lg bg-danger/10 px-3 py-2 text-danger-ink">
      <Snowflake className="h-4 w-4 shrink-0" />
      <span className="text-xs">
        Account frozen — read-only mode. Upgrade to resume operations.
      </span>
    </div>
  );
}

// ─── PlanGate Component ──────────────────────────────────────────────────────

interface PlanGateProps {
  feature?: PlanFeature;
  requireActive?: boolean;
  fallback?: ReactNode;
  hideFallback?: boolean;
  children: ReactNode;
}

export function PlanGate({
  feature,
  requireActive = false,
  fallback,
  hideFallback = false,
  children,
}: PlanGateProps): JSX.Element | null {
  const { canAccess, isFrozen, plan, requiredTierFor } = usePlanGate();

  if (requireActive && isFrozen) {
    if (hideFallback) return null;
    if (fallback) return <>{fallback}</>;
    return <FrozenBanner />;
  }

  if (feature && !canAccess(feature)) {
    if (hideFallback) return null;
    if (fallback) return <>{fallback}</>;
    const requiredTier = requiredTierFor(feature) ?? PLAN_TIERS.BASIC;
    return (
      <UpgradePrompt
        feature={feature}
        requiredTier={requiredTier}
        currentTier={plan.planTier}
      />
    );
  }

  return <>{children}</>;
}
